import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ButtonBase, styled, Typography } from '@ori-ui/mui';
import { Form, Formik } from 'formik';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { CHANGE_PASSWORD_URL, DO_LOGIN_URL, VALIDATE_LOGIN_URL } from '../../constants';
import { useConfigurationContext } from '../../contexts/ConfigurationContext';
import { useUserCredentialsContext } from '../../contexts/UserCredentialsContext';
import { buildPasswordRequirementsSchema, getCustomAxios } from '../../helpers';
import { useChangePasswordMetadataQuery } from '../../models/api';
import { LocalizationKeys } from '../../models/localizationKeys';
import { Loading } from '../Loading';
import { PasswordInput } from './PasswordInput';
import { PasswordRequirements } from './PasswordRequirements';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var NewPasswordSuperUserRoot = styled('div')(function (_ref) {
  var theme = _ref.theme;
  return {
    overflowY: 'auto',
    height: 'calc(100% - var(--top-nav-height))',
    padding: theme.spacing(3, 4)
  };
});
var NewPasswordSuperUserHeader = styled(Typography)(function (_ref2) {
  var theme = _ref2.theme;
  return {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center'
  };
});
var NewPasswordSuperUserError = styled('div')(function (_ref3) {
  var theme = _ref3.theme;
  return {
    color: theme.palette.error.main
  };
});
var NewPasswordSuperUserSubmit = styled(ButtonBase)(function (_ref4) {
  var theme = _ref4.theme;
  return {
    background: theme.palette.primary.main,
    borderRadius: 20,
    color: theme.palette.primary.contrastText,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2, 0),
    textAlign: 'center',
    textTransform: 'uppercase',
    width: '100%'
  };
});
export var NewPasswordSuperUser = function NewPasswordSuperUser() {
  var formRef = useRef(null);

  var _useTranslation = useTranslation(),
      _useTranslation2 = _slicedToArray(_useTranslation, 1),
      translate = _useTranslation2[0];

  var _useUserCredentialsCo = useUserCredentialsContext(),
      Username = _useUserCredentialsCo.customerID,
      OldPassword = _useUserCredentialsCo.password;

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      errorMessage = _useState2[0],
      setErrorMessage = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isSubmitLocked = _useState4[0],
      setIsSubmitLocked = _useState4[1];

  var _useConfigurationCont = useConfigurationContext(),
      currentLanguage = _useConfigurationCont.currentLanguage,
      onlineBaseUrl = _useConfigurationCont.onlineBaseUrl;

  var _useState5 = useState(true),
      _useState6 = _slicedToArray(_useState5, 2),
      isInUnknownState = _useState6[0],
      setIsInUnknownState = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      arePasswordRequirementsShown = _useState8[0],
      setArePasswordRequirementsShown = _useState8[1];

  var changePasswordUrl = new URL(CHANGE_PASSWORD_URL, onlineBaseUrl !== null && onlineBaseUrl !== void 0 ? onlineBaseUrl : window.location.origin);
  var doLoginUrl = new URL(DO_LOGIN_URL, onlineBaseUrl !== null && onlineBaseUrl !== void 0 ? onlineBaseUrl : window.location.origin);
  var validateLoginUrl = new URL(VALIDATE_LOGIN_URL, onlineBaseUrl !== null && onlineBaseUrl !== void 0 ? onlineBaseUrl : window.location.origin);
  var metadata = useChangePasswordMetadataQuery();
  var isLoading = !metadata;

  var getPasswordRequirements = function getPasswordRequirements() {
    var _metadata$newPassword, _metadata$newPassword2, _metadata$newPassword3, _metadata$newPassword4, _metadata$newPassword5, _metadata$newPassword6, _metadata$newPassword7, _metadata$newPassword8, _metadata$newPassword9, _metadata$newPassword11, _metadata$newPassword12, _metadata$newPassword13, _metadata$newPassword15, _metadata$newPassword16, _metadata$newPassword17, _metadata$newPassword19, _metadata$newPassword20, _metadata$newPassword21, _metadata$newPassword23, _metadata$newPassword24, _metadata$newPassword25;

    var minLength = (_metadata$newPassword = metadata === null || metadata === void 0 ? void 0 : (_metadata$newPassword2 = metadata.newPassword) === null || _metadata$newPassword2 === void 0 ? void 0 : (_metadata$newPassword3 = _metadata$newPassword2.validation) === null || _metadata$newPassword3 === void 0 ? void 0 : _metadata$newPassword3.minLength) !== null && _metadata$newPassword !== void 0 ? _metadata$newPassword : 6;
    var maxLength = (_metadata$newPassword4 = metadata === null || metadata === void 0 ? void 0 : (_metadata$newPassword5 = metadata.newPassword) === null || _metadata$newPassword5 === void 0 ? void 0 : (_metadata$newPassword6 = _metadata$newPassword5.validation) === null || _metadata$newPassword6 === void 0 ? void 0 : _metadata$newPassword6.maxLength) !== null && _metadata$newPassword4 !== void 0 ? _metadata$newPassword4 : 255;
    var requirements = [];

    if (metadata !== null && metadata !== void 0 && (_metadata$newPassword7 = metadata.newPassword) !== null && _metadata$newPassword7 !== void 0 && (_metadata$newPassword8 = _metadata$newPassword7.requirements) !== null && _metadata$newPassword8 !== void 0 && (_metadata$newPassword9 = _metadata$newPassword8.letter) !== null && _metadata$newPassword9 !== void 0 && _metadata$newPassword9.isRequired) {
      var _metadata$newPassword10;

      requirements.push({
        key: 'letter-requirement',
        localizedText: translate(LocalizationKeys.passwordRequirementLetter.id),
        validationRegex: new RegExp((_metadata$newPassword10 = metadata.newPassword.requirements.letter.regularExpression) !== null && _metadata$newPassword10 !== void 0 ? _metadata$newPassword10 : '')
      });
    }

    if (metadata !== null && metadata !== void 0 && (_metadata$newPassword11 = metadata.newPassword) !== null && _metadata$newPassword11 !== void 0 && (_metadata$newPassword12 = _metadata$newPassword11.requirements) !== null && _metadata$newPassword12 !== void 0 && (_metadata$newPassword13 = _metadata$newPassword12.lowercaseLetter) !== null && _metadata$newPassword13 !== void 0 && _metadata$newPassword13.isRequired) {
      var _metadata$newPassword14;

      requirements.push({
        key: 'lower-case-requirement',
        localizedText: translate(LocalizationKeys.passwordRequirementLowerCaseLetter.id),
        validationRegex: new RegExp((_metadata$newPassword14 = metadata.newPassword.requirements.lowercaseLetter.regularExpression) !== null && _metadata$newPassword14 !== void 0 ? _metadata$newPassword14 : '')
      });
    }

    if (metadata !== null && metadata !== void 0 && (_metadata$newPassword15 = metadata.newPassword) !== null && _metadata$newPassword15 !== void 0 && (_metadata$newPassword16 = _metadata$newPassword15.requirements) !== null && _metadata$newPassword16 !== void 0 && (_metadata$newPassword17 = _metadata$newPassword16.uppercaseLetter) !== null && _metadata$newPassword17 !== void 0 && _metadata$newPassword17.isRequired) {
      var _metadata$newPassword18;

      requirements.push({
        key: 'upper-case-requirement',
        localizedText: translate(LocalizationKeys.passwordRequirementUpperCaseLetter.id),
        validationRegex: new RegExp((_metadata$newPassword18 = metadata.newPassword.requirements.uppercaseLetter.regularExpression) !== null && _metadata$newPassword18 !== void 0 ? _metadata$newPassword18 : '')
      });
    }

    requirements.push({
      key: 'length-requirement',
      localizedText: translate(LocalizationKeys.passwordRequirementCharactersCount.id, {
        0: minLength
      }),
      validationRegex: new RegExp("^.{".concat(minLength, ",").concat(maxLength, "}$"))
    });

    if (metadata !== null && metadata !== void 0 && (_metadata$newPassword19 = metadata.newPassword) !== null && _metadata$newPassword19 !== void 0 && (_metadata$newPassword20 = _metadata$newPassword19.requirements) !== null && _metadata$newPassword20 !== void 0 && (_metadata$newPassword21 = _metadata$newPassword20.digit) !== null && _metadata$newPassword21 !== void 0 && _metadata$newPassword21.isRequired) {
      var _metadata$newPassword22;

      requirements.push({
        key: 'digit-requirement',
        localizedText: translate(LocalizationKeys.passwordRequirementDigit.id),
        validationRegex: new RegExp((_metadata$newPassword22 = metadata.newPassword.requirements.digit.regularExpression) !== null && _metadata$newPassword22 !== void 0 ? _metadata$newPassword22 : '')
      });
    }

    if (metadata !== null && metadata !== void 0 && (_metadata$newPassword23 = metadata.newPassword) !== null && _metadata$newPassword23 !== void 0 && (_metadata$newPassword24 = _metadata$newPassword23.requirements) !== null && _metadata$newPassword24 !== void 0 && (_metadata$newPassword25 = _metadata$newPassword24.specialCharacter) !== null && _metadata$newPassword25 !== void 0 && _metadata$newPassword25.isRequired) {
      var _metadata$newPassword26;

      requirements.push({
        key: 'special-character-requirement',
        localizedText: translate(LocalizationKeys.passwordRequirementSpecialCharacter.id),
        validationRegex: new RegExp((_metadata$newPassword26 = metadata.newPassword.requirements.specialCharacter.regularExpression) !== null && _metadata$newPassword26 !== void 0 ? _metadata$newPassword26 : '')
      });
    }

    return requirements;
  };

  var passwordRequirements = getPasswordRequirements();
  var passwordSchema = buildPasswordRequirementsSchema(passwordRequirements);
  var passSchema = Yup.object({
    NewPassword: passwordSchema,
    ConfirmNewPassword: Yup.string().oneOf([Yup.ref('NewPassword'), null], 'Passwords must match')
  });

  var submitLogin = function submitLogin() {
    var validationSuccess = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    if (validationSuccess) {
      try {
        if (!formRef.current) {
          return;
        }

        formRef.current.submit();
      } catch (_unused) {
        setErrorMessage(translate(LocalizationKeys.errorWhenSettingUpPassword.id));
        setIsSubmitLocked(false);
      }
    }
  };

  var onChangeForgottenPassSubmit = // eslint-disable-next-line unicorn/consistent-function-scoping -- function currying
  function onChangeForgottenPassSubmit() {
    return async function (values, actions) {
      actions.setSubmitting(false);
      setErrorMessage(null);

      if (!isSubmitLocked) {
        setIsSubmitLocked(true);

        try {
          var _await$getCustomAxios = await getCustomAxios(currentLanguage).put(changePasswordUrl.toString(), {
            NewPassword: values.NewPassword,
            OldPassword: OldPassword,
            Username: Username
          }),
              _await$getCustomAxios2 = _await$getCustomAxios.data,
              Success = _await$getCustomAxios2.Success,
              Message = _await$getCustomAxios2.Message;

          if (Success) {
            try {
              var loginValidateResponse = await getCustomAxios(currentLanguage).post(validateLoginUrl.toString(), {
                Username: Username,
                Password: values.NewPassword
              });
              submitLogin(loginValidateResponse.data.Success);
            } catch (_unused2) {
              setErrorMessage(translate(LocalizationKeys.errorWhenSettingUpPassword.id));
              setIsSubmitLocked(false);
            }
          } else {
            setErrorMessage(Message);
          }
        } catch (_unused3) {
          setErrorMessage(translate(LocalizationKeys.errorWhenSettingUpPassword.id));
          setIsSubmitLocked(false);
        }
      }
    };
  };

  return /*#__PURE__*/_jsxs(NewPasswordSuperUserRoot, {
    children: [/*#__PURE__*/_jsx(NewPasswordSuperUserHeader, {
      variant: "h6",
      children: translate(LocalizationKeys.logIn.id)
    }), /*#__PURE__*/_jsx(Formik, {
      initialValues: {
        ConfirmNewPassword: '',
        NewPassword: '',
        Password: '',
        Username: ''
      },
      validationSchema: passSchema,
      onSubmit: onChangeForgottenPassSubmit(),
      children: function children(_ref5) {
        var _errors$NewPassword;

        var values = _ref5.values,
            errors = _ref5.errors,
            validateField = _ref5.validateField,
            setFieldTouched = _ref5.setFieldTouched;
        return isLoading ? /*#__PURE__*/_jsx(Loading, {}) : /*#__PURE__*/_jsxs(Form, {
          ref: formRef,
          action: doLoginUrl.toString(),
          method: "POST",
          children: [errorMessage && /*#__PURE__*/_jsx(NewPasswordSuperUserError, {
            children: errorMessage
          }), /*#__PURE__*/_jsx("input", {
            hidden: true,
            readOnly: true,
            name: "Username",
            type: "text",
            value: Username
          }), /*#__PURE__*/_jsx("input", {
            hidden: true,
            readOnly: true,
            name: "Password",
            type: "password",
            value: values.NewPassword
          }), /*#__PURE__*/_jsx(PasswordInput, {
            passwordRequirementsValidation: true,
            autoComplete: "new-password",
            name: "NewPassword",
            type: "password",
            handleKeyUp: function handleKeyUp() {
              if (isInUnknownState) {
                setIsInUnknownState(false);
              }

              setFieldTouched('NewPassword');
              validateField('NewPassword');
            },
            handleOnBlur: function handleOnBlur() {
              if (isInUnknownState) {
                setIsInUnknownState(false);
              }
            },
            handleOnFocus: function handleOnFocus() {
              setArePasswordRequirementsShown(true);
            }
          }), arePasswordRequirementsShown || errors.NewPassword ? /*#__PURE__*/_jsx(PasswordRequirements, {
            isInUnknownState: isInUnknownState,
            requirements: passwordRequirements,
            unfulfilledRequirementKeys: (_errors$NewPassword = errors.NewPassword) === null || _errors$NewPassword === void 0 ? void 0 : _errors$NewPassword.split('|').filter(function (element) {
              return element;
            })
          }) : null, /*#__PURE__*/_jsx(PasswordInput, {
            autoComplete: "new-password",
            name: "ConfirmNewPassword",
            type: "password"
          }), /*#__PURE__*/_jsx(NewPasswordSuperUserSubmit, {
            disableRipple: true,
            type: "submit",
            onClick: function onClick() {
              if (isInUnknownState) {
                setIsInUnknownState(false);
              }
            },
            children: translate(LocalizationKeys.signIn.id)
          })]
        });
      }
    })]
  });
};